/**
 * Axios instance for External Portal API calls.
 *
 * This is a dedicated axios instance that ONLY uses external portal tokens.
 * It does NOT fall back to internal user tokens, preventing issues when
 * a user has both internal and external tokens in localStorage.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../../shared/api/config';
import { EXTERNAL_TOKEN_KEY, EXTERNAL_REFRESH_TOKEN_KEY } from './externalAuthStorage';

// Custom event for external session expiration
export const EXTERNAL_SESSION_EXPIRED_EVENT = 'panluma:external-session-expired';

export const emitExternalSessionExpired = () => {
  window.dispatchEvent(new CustomEvent(EXTERNAL_SESSION_EXPIRED_EVENT));
};

// Create dedicated axios instance for external portal
const externalAxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add external token
externalAxiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(EXTERNAL_TOKEN_KEY);

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 for external users
externalAxiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Handle 401 - external session expired
    if (error.response?.status === 401) {
      // Clear external tokens only
      localStorage.removeItem(EXTERNAL_TOKEN_KEY);
      localStorage.removeItem(EXTERNAL_REFRESH_TOKEN_KEY);
      emitExternalSessionExpired();
    }

    return Promise.reject(error);
  }
);

export default externalAxiosInstance;
